import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ClipboardList, 
  User, 
  Calendar,
  Loader2,
  Plus,
  Edit2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs?limit=100`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'transaction_created':
      case 'merchant_created':
      case 'staff_added':
        return <Plus className="w-4 h-4 text-emerald-600" />;
      case 'status_changed':
        return <Edit2 className="w-4 h-4 text-blue-600" />;
      default:
        return <ClipboardList className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'transaction_created':
      case 'merchant_created':
      case 'staff_added':
        return 'bg-emerald-50 border-emerald-200';
      case 'status_changed':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0077B6]" />
      </div>
    );
  }

  return (
    <div data-testid="audit-log-page" className="animate-fadeIn">
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -4px rgba(0,0,0,0.1); }
        .log-item { transition: all 0.2s ease; }
        .log-item:hover { background: linear-gradient(90deg, transparent 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%); }
      `}</style>
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#0A1628]">Audit Log</h1>
        <p className="text-slate-500 mt-1">
          Track all actions and changes in your merchant account
        </p>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover-lift">
        {logs.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
              <ClipboardList className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">No audit logs yet</h3>
            <p className="text-slate-500">Actions will appear here once you start using FlowLedger</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log, idx) => (
              <div 
                key={log.log_id} 
                className="p-4 log-item cursor-pointer" 
                data-testid={`log-${log.log_id}`}
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 transition-transform duration-300 hover:scale-110 ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">
                      {log.details}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.performed_by_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(log.created_at)}
                      </span>
                      {log.transaction_id && (
                        <span className="font-mono text-[#0077B6]">
                          {log.transaction_id}
                        </span>
                      )}
                    </div>
                  </div>

                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    log.action.includes('created') ? 'bg-emerald-100 text-emerald-700' :
                    log.action.includes('changed') ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {log.action.replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
